import * as Stimulus from "@hotwired/stimulus"

/**
 * Keeps the Schedule dialog's submit button off until the send date and time
 * are one the server would accept, and says under the field what is wrong. See
 * the schedule action in App.Handler.Admin.Invites.Outbox.
 *
 * The server still refuses a bad time; this only saves the round trip. A date
 * range that closes before the send time is left to the server, because each
 * selected row has its own range.
 */
export class SendTimeController extends Stimulus.Controller {
    static get targets() {
        return [ "date", "time", "dateError", "timeError" ];
    }

    static get values() {
        return { timeZone: String };
    }

    connect() {
        this.validate();
        // A dialog left open can go stale as the clock passes the picked time.
        this.timer = setInterval(() => this.validate(), 30000);
    }

    disconnect() {
        clearInterval(this.timer);
    }

    // The button sits in the dialog's footer, which the bulk bar draws outside
    // the fields this controller is given.
    get submitButton() {
        return this.element.closest("dialog")?.querySelector("button[type=submit]");
    }

    validate() {
        const now = this.zonedNow();
        this.dateTarget.min = now.date;

        const date = this.dateTarget.value;
        const time = this.timeTarget.value;
        let dateError = "";
        let timeError = "";

        if (date && date < now.date) {
            dateError = "Pick today or a later date.";
        } else if (date === now.date && time <= now.time) {
            timeError = "Time cannot be in the past";
        }

        this.show(this.dateTarget, this.dateErrorTarget, dateError);
        this.show(this.timeTarget, this.timeErrorTarget, timeError);

        const button = this.submitButton;
        if (button) {
            button.disabled = !date || Boolean(dateError || timeError);
        }
    }

    show(input, error, message) {
        error.textContent = message;
        error.hidden = !message;
        input.setAttribute("aria-invalid", message ? "true" : "false");
    }

    // Both as zero-padded strings, so they compare in order the same way the
    // inputs' own values do.
    zonedNow() {
        const parts = Object.fromEntries(
            new Intl.DateTimeFormat("en-US", {
                timeZone: this.timeZoneValue,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hourCycle: "h23",
            })
                .formatToParts(new Date())
                .map(({ type, value }) => [type, value])
        );
        return {
            date: `${parts.year}-${parts.month}-${parts.day}`,
            time: `${parts.hour}:${parts.minute}`,
        };
    }
}
